/* eslint-disable no-unused-vars */
import { Button, Flex, Text } from '@auviknetworks/iceberg-ui';
import { CircularProgress } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Box } from '@mui/system';
import { INTEGRATION_OPTION_GOOGLE_AUTOMATED } from 'application/Shared/Components/Client';
import { ChooseGroups } from 'application/Shared/Components/Client/Steps/ChooseGroups';
import { Notify, Route } from 'application/Shared/Helpers';
import { ProviderType } from 'application/Shared/types/tenantProvider';
import axios from 'axios';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router';

const SubmitGroupsButton = ({
    groups,
    providerRoute,
    linkedTenantId,
    navigate
}) => {
    const { t } = useTranslation();
    const { partnerId } = useParams();
    const basePartnerRoute = Route.buildBasePartnerRoute({ partnerId });

    const providerType =
        providerRoute === 'google'
            ? ProviderType.Google
            : ProviderType.Microsoft;
    const platformTenantId = linkedTenantId;

    const [isSubmitting, setIsSubmitting] = useState(false);

    const onClick = async () => {
        setIsSubmitting(true);
        // get all values from the groups object and make an api call
        // to submit the groups.
        const promises = Object.values(groups).map(async (group) => {
            const { exists, id, isOrgUnit } = group;

            /* params */
            const params = {
                excluded: true,
                id,
                isOrgUnit,
                platformTenantId
            };

            try {
                // This case is a failsafe. The frontend doesn't display the checkbox
                // if the group is already in the database for the tenant.
                if (exists) {
                    // submit the group
                    return await axios.put(
                        `/${providerRoute}/tenantGroup/${id}`,
                        params
                    );
                }
                // create the group
                return await axios.post(
                    `/${providerRoute}/tenantGroup`,
                    params
                );
            } catch (error) {
                return error.response;
            }
        });

        const responses = await Promise.all(promises);
        const success = responses.every((response) => response.status < 400);
        if (success) {
            await axios.post(`/tenantProvider/quickscan`, {
                platformTenantId,
                providerType
            });
            if (Object.values(groups).length) {
                Notify.s(
                    t(
                        'Provider.MicrosoftConfiguration.success_group_excluded_added'
                    )
                );
            }
            navigate(`${basePartnerRoute}/onboarding/quickScan/searchingApps`);
        } else {
            Notify.e(t('Provider.MicrosoftConfiguration.error_saving_group'));
        }
        setIsSubmitting(false);
    };
    return (
        <>
            {isSubmitting && (
                <Box
                    sx={{
                        alignContent: 'center',
                        display: 'flex',
                        flexWrap: 'wrap',
                        marginRight: '4px'
                    }}
                >
                    <CircularProgress size="32px" />
                </Box>
            )}
            <Button
                data-testid="quickScanGroupsSubmitButton"
                disabled={isSubmitting}
                onClick={onClick}
            >
                {t('Common.continue')}
            </Button>
        </>
    );
};

export const QuickScanChooseGroupsStep = () => {
    const { t } = useTranslation();
    const theme = useTheme();
    const navigate = useNavigate();
    const [groups, setGroups] = useState({});

    const { partnerId } = useParams();
    const basePartnerRoute = Route.buildBasePartnerRoute({ partnerId });

    const { search } = useLocation();
    const searchParams = new URLSearchParams(search);

    const clientId = searchParams.get('clientId');
    const provider = searchParams.get('provider');

    if (!clientId || !provider) {
        navigate(`${basePartnerRoute}/onboarding/quickScan/select`);
    }

    const providerRoute =
        provider === INTEGRATION_OPTION_GOOGLE_AUTOMATED ? 'google' : 'azure';

    return (
        <Flex
            alignItems="center"
            data-testid="onboardingQuickScanChooseGroupStep"
            flexDirection="column"
            mt={4}
            mx="4%"
        >
            <Text
                fontSize={['32px', '32px', '32px', 5]}
                color={theme.palette.onboarding.titleTextColor}
                fontWeight="600"
                textAlign="center"
            >
                {t('Onboarding.quickScanSelectGroups.title')}
            </Text>
            <Text
                fontSize={4}
                color={theme.palette.onboarding.titleTextColor}
                my={4}
                textAlign="center"
            >
                {t(`Onboarding.quickScanSelectGroups.subtitle`)}
            </Text>
            <ChooseGroups
                groups={groups}
                integrationType={provider}
                linkedTenantId={clientId}
                setGroups={setGroups}
                formFooterComponent={
                    <Flex alignSelf="center">
                        <SubmitGroupsButton
                            linkedTenantId={clientId}
                            groups={groups}
                            providerRoute={providerRoute}
                            navigate={navigate}
                        />
                    </Flex>
                }
            />
        </Flex>
    );
};
